// Local, always-available settings for sound / haptics / motion. This is the
// source of truth the juice engine reads, so feedback works even before login.
// When a user signs in we hydrate these from their profile and mirror changes.

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { DEFAULT_TRANSLATION_ID } from '@/lib/translations'

interface SettingsState {
  soundEnabled: boolean
  hapticsEnabled: boolean
  reduceMotion: boolean
  volume: number // 0..1
  /** Background music. Independent of soundEnabled on purpose — "music off,
   *  sound effects on" is the combination most people reach for. */
  musicEnabled: boolean
  musicVolume: number // 0..1
  /** Whether the "build your character" nudge has been dismissed (one-time). */
  characterPromptDismissed: boolean
  /** Whether the how-to-play walkthrough has been shown once automatically. */
  tutorialSeen: boolean
  /** First-visit tips already closed, by tab (`FirstVisitTip`). Grows once per
   *  tab and never shrinks; a closed tip leaves no other trace. */
  tipsSeen: string[]
  /** The three-places sheet after the very first daily verse (one-time). */
  firstDropTourSeen: boolean
  /** The arcade lobby has been opened once — its Play-tab cue stops. */
  arcadeLobbySeen: boolean
  /** The Bible map has been opened once — its "verses lit" cue stops. */
  bibleMapSeen: boolean
  /** The day-2-3 church moment: 'new' (not offered), 'found' (sent to the
   *  picker; one give offer may follow), 'done' (completed or dismissed). */
  churchMoment: 'new' | 'found' | 'done'
  /** Whether the "add to home screen" nudge has been dismissed (one-time). */
  installPromptDismissed: boolean
  /**
   * Whether the player has ever had the Inventory section open in front of
   * them. Set from InventorySection itself, which only mounts when the section
   * is actually expanded — so this is "saw it", not "walked past it".
   */
  inventorySeen: boolean
  /** Whether the "you're holding relics" nudge has been dismissed (one-time). */
  inventoryNudgeDismissed: boolean
  /** When the App Store bubble was last dismissed (epoch ms, 0 = never). */
  appNudgeSnoozedAt: number
  /** Set once they've tapped through to the store — then we stop asking. */
  appNudgeDone: boolean
  /** The Bible this player reads in — a lib/translations id. Every surface
   *  that quotes a verse reads it; the daily quiz alone keeps the BSB it asks
   *  about. 'web', 'kjv' and 'bbe' are the old bible-api codes, still valid. */
  readingTranslation: string
  /** Which of the device's two chosen voices reads scripture aloud. Shared
   *  with the prayer sheet on purpose: it is one person's preferred reading
   *  voice, not a per-feature setting. */
  readingVoice: 'female' | 'male'
  /**
   * How big the verse text is inside the book. 1 is the drawn default; the
   * chapter reader multiplies its own sizes by this.
   *
   * It lives here rather than on the profile for the same reason `volume` does:
   * it is a property of the eyes looking at THIS screen, not of the account. It
   * is also the one accessibility control this app was missing — the Bible is
   * the surface people read for minutes at a time, at 16px, and 16px is not a
   * size everyone can read.
   */
  readingTextScale: number
  /** Whether the prayer sheet labels its four movements. Off by default: a
   *  first-time reader should meet a prayer, not a diagram. */
  prayerShowShape: boolean
  /** Which reading voice the prayer sheet uses — see lib/voice.ts. The device's
   *  own voices; this only chooses between them. */
  prayerVoice: 'female' | 'male'
  set: (patch: Partial<Omit<SettingsState, 'set'>>) => void
}

export const useSettings = create<SettingsState>()(
  persist(
    (set) => ({
      soundEnabled: true,
      hapticsEnabled: true,
      reduceMotion: false,
      volume: 0.6,
      musicEnabled: true,
      musicVolume: 0.55,
      characterPromptDismissed: false,
      tutorialSeen: false,
      tipsSeen: [],
      firstDropTourSeen: false,
      arcadeLobbySeen: false,
      bibleMapSeen: false,
      churchMoment: 'new',
      installPromptDismissed: false,
      inventorySeen: false,
      inventoryNudgeDismissed: false,
      appNudgeSnoozedAt: 0,
      appNudgeDone: false,
      readingTranslation: DEFAULT_TRANSLATION_ID,
      readingVoice: 'female',
      readingTextScale: 1,
      prayerShowShape: false,
      prayerVoice: 'female',
      set: (patch) => set(patch),
    }),
    { name: 'va.settings', storage: createJSONStorage(() => localStorage) },
  ),
)
